import Label from "components/Label/Label";
import React, { FC, useEffect, useState } from "react";
import Avatar from "shared/Avatar/Avatar";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Input from "shared/Input/Input";
import CommonLayout from "./CommonLayout";
import { Helmet } from "react-helmet";
import { getProfileFun, ProfileChangeFun } from "api/profile/profile";
import { profileChangePayload } from "api/types";
import toast from "react-hot-toast";
import { useData } from "data/data-provider";

export interface AccountPageProps {
  className?: string;
}

const AccountPage: FC<AccountPageProps> = ({ className = "" }) => {
  const { user, setUser } = useData();
  const [profile, setProfile] = useState<profileChangePayload | null>(null);
  const [originalProfile, setOriginalProfile] =
    useState<profileChangePayload | null>(null); // Store the original profile
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    const response = await getProfileFun();
    if (response.data) {
      setProfile(response.data);
      setOriginalProfile(response.data); // Store the original profile
    }
  };

  // Update this function to check if profile has changed or if a new file is uploaded
  const hasProfileChanged = (): boolean => {
    const profileChanged =
      JSON.stringify(profile) !== JSON.stringify(originalProfile);
    const fileChanged = file !== null; // Check if a file is uploaded
    return profileChanged || fileChanged; // Enable button if profile changed or file uploaded
  };

  const handleProfileUpdate = async () => {
    if (!profile) return;

    setLoading(true);

    const formData = new FormData();
    formData.append("first_name", profile.first_name || "");
    formData.append("last_name", profile.last_name || "");
    formData.append("phone_no", profile.phone_no || "");

    if (file) {
      formData.append("profile_picture", file); // Append the file to FormData
    } else {
      formData.append("profile_picture", profile?.profile_picture || "");
    }

    const response: any = await ProfileChangeFun(formData);
    if (response.data) {
      setProfile(response?.data?.data);
      setUser({
        ...user,
        profile_picture: response?.data?.data?.profile_picture,
        email: response.data?.data?.email,
      });
      setOriginalProfile(response?.data?.data);
      toast.success("Profile updated successfully");
    } else {
      toast.error("There was an error updating profile");
    }

    setFile(null); // Reset the file state after upload
    setLoading(false);
  };

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile); // Update file state when a file is selected
    }
  };

  return (
    <div className={`nc-AccountPage ${className}`} data-nc-id="AccountPage">
      <Helmet>
        <title>Account</title>
      </Helmet>
      <CommonLayout>
        <div className="space-y-6 sm:space-y-8">
          {/* HEADING */}
          <h2 className="text-3xl font-semibold">Account information</h2>
          <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
          <div className="flex flex-col md:flex-row">
            <div className="flex-shrink-0 flex items-start">
              <div className="relative rounded-full overflow-hidden flex">
                <Avatar
                  sizeClass="w-32 h-32"
                  imgUrl={profile?.profile_picture || ""}
                />
                <div className="absolute inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center text-neutral-50 cursor-pointer">
                  <svg
                    width="30"
                    height="30"
                    viewBox="0 0 30 30"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17.5 5H7.5C6.83696 5 6.20107 5.26339 5.73223 5.73223C5.26339 6.20107 5 6.83696 5 7.5V20M5 20V22.5C5 23.163 5.26339 23.7989 5.73223 24.2678C6.20107 24.7366 6.83696 25 7.5 25H22.5C23.163 25 23.7989 24.7366 24.2678 24.2678C24.7366 23.7989 25 23.163 25 22.5V17.5M5 20L10.7325 14.2675C11.2013 13.7988 11.8371 13.5355 12.5 13.5355C13.1629 13.5355 13.7987 13.7988 14.2675 14.2675L17.5 17.5M25 12.5V17.5M25 17.5L23.0175 15.5175C22.5487 15.0488 21.9129 14.7855 21.25 14.7855C20.5871 14.7855 19.9513 15.0488 19.4825 15.5175L17.5 17.5M17.5 17.5L20 20M22.5 5H27.5M25 2.5V7.5M17.5 10H17.5125"
                      stroke="currentColor"
                      strokeWidth={1.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>

                  <span className="mt-1 text-xs">Change Image</span>
                </div>
                <input
                  type="file"
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  onChange={handleFileChange} // Handle file input change
                />
              </div>
            </div>
            <div className="flex-grow mt-10 md:mt-0 md:pl-16 max-w-3xl space-y-6">
              <div>
                <label>First Name</label>
                <Input
                  className="mt-1.5"
                  value={profile?.first_name || ""}
                  onChange={(e) =>
                    setProfile({ ...profile, first_name: e.target.value })
                  }
                />
              </div>
              <div>
                <label>Last Name</label>
                <Input
                  className="mt-1.5"
                  value={profile?.last_name || ""}
                  onChange={(e) =>
                    setProfile({ ...profile, last_name: e.target.value })
                  }
                />
              </div>
              <div>
                <label>Phone number</label>
                <Input
                  className="mt-1.5"
                  value={profile?.phone_no || ""}
                  onChange={(e) =>
                    setProfile({ ...profile, phone_no: e.target.value })
                  }
                />
              </div>
              <div>
                <label>Email</label>
                <Input
                  className="mt-1.5"
                  value={profile?.email || ""}
                  disabled={true} // Email is not editable
                />
              </div>
              <div className="pt-2">
                <ButtonPrimary
                  onClick={handleProfileUpdate}
                  className={`min-w-[150px] bg-[#FD2400] rounded-lg ${
                    !hasProfileChanged() || loading
                      ? "cursor-not-allowed"
                      : "cursor-pointer"
                  }`}
                  disabled={!hasProfileChanged() || loading} // Enable button if profile has changed or file is uploaded
                >
                  {loading ? (
                    <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-gray-300"></div>
                  ) : (
                    "Update info"
                  )}
                </ButtonPrimary>
              </div>
            </div>
          </div>
        </div>
      </CommonLayout>
    </div>
  );
};

export default AccountPage;
